import {
  CustomerCompany,
  CustomerCompanyListFilter,
} from 'module/api/company/types';
import { QueryCombinerType } from 'module/api/filter/types';
import { useDebounce } from 'module/hooks/use-debounce';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { FieldFilter } from 'module/types/filter-field/filter';
import {
  GridReadyEvent,
  GridApi,
  IServerSideGetRowsParams,
  SortModelItem,
} from 'ag-grid-community';
import { CompanyAPI } from 'api/company';
//import { CustomerCompanyAPI } from 'api/customer-company';
import { keyBy } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { getFieldString } from 'utils/field-string';

const DEFAULT_SORT_FIELD = 'signal_occurrence*companyId.desc';

const toggleEmptyState = (gridApi?: GridApi) => {
  if (gridApi) {
    gridApi.refreshHeader();

    if (gridApi.getDisplayedRowCount() < 1) {
      gridApi.showNoRowsOverlay();
    } else {
      gridApi.hideOverlay();
    }
  }
};

type FieldLookupById = Record<number, FilterableField>;

export const getFieldMapReducer =
  (fieldLookup: FieldLookupById) =>
  (
    hash: Record<string, unknown>,
    {
      fieldValue,
      fieldId,
    }: { fieldId: number; fieldValue: unknown; isEditable?: boolean },
  ) => {
    const field = fieldLookup[fieldId];

    if (field) {
      hash[field.fieldName] = fieldValue;
    }

    return hash;
  };

const getSortAsString = (
  lookup: Record<string, FilterableField>,
  sortOrder?: SortModelItem[],
): string => {
  const [sortedField] = sortOrder || [];

  if (!sortedField) {
    return DEFAULT_SORT_FIELD;
  }
  const sortColName = sortedField?.colId ?? '';

  if (Object.keys(lookup).length <= 0) {
    return DEFAULT_SORT_FIELD;
  }

  const field = lookup[sortColName];

  if (!field) {
    return DEFAULT_SORT_FIELD;
  }

  return `${getFieldString(field)}.${sortedField.sort}`;
};

const transformIntoRowData = (
  customerCompanies: CustomerCompany[],
  fieldsLookupById: Record<string, FilterableField>,
) => {
  const toFieldMap = getFieldMapReducer(fieldsLookupById);

  return customerCompanies.map((company) => ({
    ...(company.fields ?? []).reduce(toFieldMap, {}),
    ...company,
    accountOwner: company?.accountOwner?.userId,
    ...company?.financialMetrics, // Pull metrics out of object for rowData
    //fieldsAccessMetaInBase3: fieldAccessMetaToBase3(company.fieldsAccessMeta),
    // Ensure new signal fields are available for rendering
    recentRiskSignals: company.recentRiskSignals,
    recentRiskSignalWeeklyCounts: company.recentRiskSignalWeeklyCounts,
    recentOpportunitySignals: company.recentOpportunitySignals,
    recentOpportunitySignalWeeklyCounts:
      company.recentOpportunitySignalWeeklyCounts,
    recentOperationalSignals: company.recentOperationalSignals,
    recentOperationalSignalWeeklyCounts:
      company.recentOperationalSignalWeeklyCounts,
  }));
};

export const textSearchAsString = (textSearch: string) => {
  return textSearch ? encodeURIComponent(textSearch) : '';
};

interface Props {
  filter?: CustomerCompanyListFilter;
  fieldsLookupByName: Record<string, FilterableField>;
  gridApi?: GridApi;
  searchText?: string;
}
/**
 * @description Hook to fetch server side data for the company list ag grid
 */
export const useServerSideData = ({
  fieldsLookupByName,
  filter,
  searchText = '',
  gridApi,
}: Props) => {
  const filterRef = useRef(filter);

  /*useEffect(() => {
    filterRef.current = filter;
  }, [filter]);*/

  const searchTextRef = useRef(searchText);

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);
  const debouncedSearch = useDebounce(searchText, 300);

  useEffect(() => {
    gridApi?.refreshServerSide({ purge: true });
  }, [debouncedSearch, gridApi]);

  return useCallback(
    (event: GridReadyEvent) => {
      event.api.setGridOption('serverSideDatasource', {
        getRows: (params: IServerSideGetRowsParams) => {
          const { request, success, fail, api } = params;
          const { endRow, startRow, sortModel } = request;
          const validFilter = filterRef?.current?.criteria.filter(
            FieldFilter.isValid,
          );

          let payload = {
            filter: {
              criteria: validFilter ?? [],
              type: filterRef?.current?.type ?? QueryCombinerType.All,
              range: filterRef?.current?.range,
            },
            searchText: textSearchAsString(searchTextRef.current),
            sort: getSortAsString(fieldsLookupByName, sortModel),
            startRow: startRow ?? 0,
            endRow: endRow ?? 100,
          };

          CompanyAPI.getPaginatedCompanies(payload)
            .then(({ count, data }) => {
              const res = transformIntoRowData(
                data,
                keyBy(Object.values(fieldsLookupByName), 'fieldId'),
              );

              success({ rowData: res, rowCount: count });
              toggleEmptyState(api);
            })
            .catch(() => {
              //logger.log('error downloading content', e);
              fail();
            });
        },
      });
    },
    [fieldsLookupByName, searchTextRef],
  );
};
